import React from 'react'
import TrackChart from './track-chart'
import {
  metersToDisplayDistance, metersToElev, mpsToSpeed,
  distanceUnit, elevUnit, speedUnit, formatDuration
} from '../../../../../lib/trackStats'

const fmtDateLine = (startS, stopS) => {
  if (!startS) return ''
  try {
    const d = new Date(startS * 1000)
    const date = d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })
    const t0 = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    const t1 = stopS ? new Date(stopS * 1000).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : ''
    return t1 ? `${date} · ${t0} – ${t1}` : date
  } catch (e) { return '' }
}

const Stat = ({ label, value, unit, valueColor }) => (
  <div className="td-stat">
    <div className="td-stat-label">{label}</div>
    <div className="td-stat-value" style={valueColor ? { color: valueColor } : null}>
      {value}{unit ? <span className="td-stat-unit">{unit}</span> : null}
    </div>
  </div>
)

const TrackView = (props) => {
  const { name, units, stats, viewPoints, color, startTimeS, stopTimeS, onHover, onClose, onEdit, onDelete } = props

  const dU = distanceUnit(units), eU = elevUnit(units), sU = speedUnit(units)
  const dist = metersToDisplayDistance(stats.distanceM || 0, units).toFixed(2)
  const dur = formatDuration(stats.realTripTimeS || stats.tripTime || 0)
  const avg = mpsToSpeed(stats.avgSpeedMps || 0, units).toFixed(1)
  const mavg = mpsToSpeed(stats.movingAvgMps || 0, units).toFixed(1)
  const top = mpsToSpeed(stats.maxSpeedMps || 0, units).toFixed(1)
  const low = Math.round(metersToElev(stats.minAltM || 0, units)).toLocaleString()
  const high = Math.round(metersToElev(stats.maxAltM || 0, units)).toLocaleString()
  const gain = Math.round(metersToElev(stats.elevationGainM || 0, units)).toLocaleString()

  const moving = formatDuration(stats.movingTimeS || 0)
  const stopped = formatDuration(stats.stoppedTimeS || 0)
  const movPct = stats.realTripTimeS > 0 ? (stats.movingTimeS / stats.realTripTimeS) * 100 : 0
  const stopCount = stats.stopCount || 0
  const longest = formatDuration(stats.longestStopS || 0)

  // Elevation axis labels.
  const elevs = viewPoints.map(p => metersToElev(p.elevM, units))
  const eHi = elevs.length ? Math.round(Math.max(...elevs)) : 0
  const eLo = elevs.length ? Math.round(Math.min(...elevs)) : 0
  const eMid = Math.round((eHi + eLo) / 2)
  const totalDisp = viewPoints.length ? metersToDisplayDistance(viewPoints[viewPoints.length - 1].distM, units) : 0
  const xLabels = [0, 0.25, 0.5, 0.75, 1].map(f => (totalDisp * f).toFixed(1))

  return (
    <div className="td-panel">
      <div className="td-header">
        <span className="td-header-title">TRACK DETAILS</span>
        <span className="td-close" onClick={onClose}>×</span>
      </div>

      <div className="td-body">
      <div className="td-name">{name}</div>
      <div className="td-dateline">{fmtDateLine(startTimeS, stopTimeS)}</div>

      <div className="td-hero-grid">
        <div className="td-hero-card">
          <div className="td-hero-value">{dist}<span className="td-hero-unit">{dU}</span></div>
          <div className="td-stat-label">Distance</div>
        </div>
        <div className="td-hero-card">
          <div className="td-hero-value">{dur}</div>
          <div className="td-stat-label">Duration</div>
        </div>
      </div>

      <div className="td-stat-grid">
        <Stat label="Avg Speed" value={avg} unit={sU} />
        <Stat label="Moving Avg" value={mavg} unit={sU} />
        <Stat label="Top Speed" value={top} unit={sU} />
        <Stat label="Low" value={low} unit={eU} />
        <Stat label="High" value={high} unit={eU} />
        <Stat label="Elev Gain" value={gain} unit={eU} valueColor="#9ECC3B" />
      </div>

      <div className="td-split">
        <div className="td-split-labels">
          <span style={{ color: '#9ECC3B' }}>MOVING {moving}</span>
          <span style={{ color: '#959595' }}>STOPPED {stopped}</span>
        </div>
        <div className="td-split-bar">
          <div className="td-split-fill" style={{ width: `${movPct}%` }} />
        </div>
        <div className="td-split-sub">{stopCount} stops · longest {longest}</div>
      </div>

      <div className="td-elev">
        <div className="td-elev-title">ELEVATION PROFILE</div>
        <div className="td-elev-row">
          <div className="td-elev-yaxis">
            <span>{eHi.toLocaleString()}</span>
            <span>{eMid.toLocaleString()}</span>
            <span>{eLo.toLocaleString()}</span>
          </div>
          <div className="td-elev-chart">
            <TrackChart points={viewPoints} units={units} mode="view" color={color} height={104} onHover={onHover} />
          </div>
        </div>
        <div className="td-elev-xaxis">
          {xLabels.map((x, i) => <span key={i}>{x}</span>)}
        </div>
        <div className="td-elev-caption">ELEV ({eU.toUpperCase()}) · DISTANCE ({dU.toUpperCase()})</div>
        <div className="td-slope-legend">
          <span className="td-slope-title">SLOPE %</span>
          {[['<4', '#86A94A'], ['4–10', '#C9A23F'], ['10–18', '#C67238'], ['18+', '#B94038']].map(([t, c], i) => (
            <span key={i} className="td-slope-entry"><i style={{ background: c }} />{t}</span>
          ))}
        </div>
      </div>
      </div>

      <div className="td-footer">
        <button className="td-btn td-btn-grey" onClick={onDelete}>DELETE</button>
        <button className="td-btn td-btn-green td-btn-primary" onClick={onEdit}>EDIT TRACK</button>
      </div>
    </div>
  )
}

export default TrackView
